<template>
  <v-dialog
    v-model="dialogOpened"
    persistent
    max-width="1000px"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        color="error"
        v-bind="attrs"
        v-on="on"
      >
        Close Case
      </v-btn>
    </template>

    <v-card
      style="position: relative;"
    >
      <v-card-title primary-title>
        <v-row>
          <v-col cols="auto">
            <span class="mr-4">Closing Case</span>
          </v-col>

          <v-spacer />

          <v-col cols="auto">
            <v-btn
              icon
              @click="dialogOpened = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-card-title>

      <div
        v-if="hasUnsentTextMessages"
        class="px-6"
      >
        <v-alert
          border="left"
          dense
          icon="mdi-alert-outline"
          type="warning"
        >
          This Case has Text Messages that have not yet been sent.<br>
          Closing a Case does not automatically cancel them, you'll have to do it manually.
        </v-alert>
      </div>

      <v-card-text>
        <v-stepper
          v-model="wizardStep"
          :elevation="0"
          vertical
        >
          <v-stepper-step
            :complete="isStepComplete(1)"
            step="1"
          >
            Closing Details
          </v-stepper-step>

          <v-stepper-content step="1">
            <CaseClosingDetails
              :legal-case="legalCase"
              @update-step-validity="valid => updateStepValidity(1, valid)"
            />

            <v-row>
              <v-spacer />

              <v-col cols="auto">
                <v-btn
                  color="primary"
                  :disabled="isCurrentStepInvalid(1)"
                  @click="nextStep"
                >
                  Continue
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-step
            :complete="isStepComplete(2)"
            step="2"
          >
            Notifications
          </v-stepper-step>

          <v-stepper-content step="2">
            <CaseClosingNotification
              :legal-case="legalCase"
              @update-step-validity="valid => updateStepValidity(2, valid)"
            />

            <v-row>
              <v-spacer />

              <v-col cols="auto">
                <v-btn
                  text
                  @click="previousStep"
                >
                  Back
                </v-btn>

                <v-btn
                  color="primary"
                  class="ml-2"
                  :disabled="isCurrentStepInvalid(2) || submitting"
                  :loading="submitting"
                  @click="submitForm"
                >
                  Proceed to close case
                </v-btn>
              </v-col>
            </v-row>
          </v-stepper-content>
        </v-stepper>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { parseErrorResponseMessage } from '@/utilities'

import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'

import CaseClosingDetails from '@/components/Dialogs/Case/CaseClosingDetails.vue'
import CaseClosingNotification from '@/components/Dialogs/Case/CaseClosingNotification.vue'

export default {
  name: 'CaseClosingWizard',

  components: {
    CaseClosingDetails,
    CaseClosingNotification,
  },

  props: {
    legalCase: {
      required: true,
      type: Object,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      dialogOpened: false,
      stepsValidity: {
        1: false,
        2: false,
      },

      submitting: false,
      unsentTextMessagesCount: 0,
      wizardStep: 1,
    }
  },

  computed: {
    hasUnsentTextMessages () {
      return this.unsentTextMessagesCount > 0
    },
  },

  watch: {
    async dialogOpened (value) {
      if (!value || !this.legalCase) return
      this.unsentTextMessagesCount = (await this.getUnsentTextMessagesInfo(this.legalCase.id)).length
    },
  },

  methods: {
    isCurrentStepInvalid (step) {
      return !this.stepsValidity[step]
    },

    isStepComplete (step) {
      return this.wizardStep > step
    },

    nextStep () {
      this.wizardStep++
    },

    previousStep () {
      this.wizardStep--
    },

    submitForm () {
      if (this.submitting) return

      this.submitting = true
      this.
        closeCase(this.legalCase.id).
        then(response => {
          this.$emit('case-closed', response.docket_entry.id)
          this.dialogOpened = false
        }).
        catch(response => this.displayAlert('error', parseErrorResponseMessage(response))).
        finally(_ => {
          this.submitting = false
        })
    },

    updateStepValidity (step, validity) {
      this.stepsValidity[step] = validity
    },

    ...mapActions(LEGAL_CASE_NAMESPACE, [
      'closeCase',
      'getUnsentTextMessagesInfo',
    ]),
  },
}
</script>
